/* eslint-disable no-lonely-if */
import React, { useState, useEffect } from 'react';
import { Menu, BookOpen } from 'lucide-react'; // For mobile menu icon and course icon
import CustomSidebar from './custom-sidebar';
import DashboardOverview from './tabs/dashboard-overview';
import ModulesTab from './tabs/modules-tab';
import LessonsTab from './tabs/lessons-tab';
import MaterialsTab from './tabs/materials-tab';
import AssessmentsTab from './tabs/assessments-tab';
import CourseForm from './forms/course-form';

export default function CourseManagementDashboard() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false); // For desktop collapse
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false); // For mobile menu open

  // State to manage the overall active view: 'dashboard', 'course-management',
  //  'create-course', 'assessments-hub', 'standalone-assessments'
  const [currentView, setCurrentView] = useState('dashboard');
  // State to manage the active tab when in 'course-management' view
  const [courseManagementActiveTab, setCourseManagementActiveTab] = useState('modules');

  // State for all courses
  const [courses, setCourses] = useState([
    {
      id: 'c1', name: 'Safety Fundamentals', description: 'An introductory course to workplace safety.', learningGoals: 'Understand basic safety principles.', objectives: 'Identify common hazards.',
    },
    {
      id: 'c2', name: 'Advanced Emergency Response', description: 'In-depth training for emergency situations.', learningGoals: 'Master emergency protocols.', objectives: 'Lead evacuation procedures.',
    },
  ]);
  // State for the currently selected course
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Dummy data for modules, lessons, materials, assessments, now associated with courses
  const [allModules, setAllModules] = useState([
    {
      id: 'm1', courseId: 'c1', name: 'Module 1: Intro to Safety', description: 'Overview of safety.',
    },
    {
      id: 'm2', courseId: 'c1', name: 'Module 2: Hazard Control', description: 'Controlling hazards.',
    },
    {
      id: 'm3', courseId: 'c2', name: 'Module A: Emergency Planning', description: 'Planning for emergencies.',
    },
  ]);
  const [allLessons, setAllLessons] = useState([
    {
      id: 'l1', courseId: 'c1', moduleId: 'm1', name: 'Lesson 1.1: What is Safety?', description: 'Defining safety.', loomVideoUrl: 'https://www.loom.com/share/example1',
    },
    {
      id: 'l2', courseId: 'c1', moduleId: 'm1', name: 'Lesson 1.2: Safety Culture', description: 'Building a safety culture.', loomVideoUrl: 'https://www.loom.com/share/example2',
    },
    {
      id: 'l3', courseId: 'c2', moduleId: 'm3', name: 'Lesson A.1: Evacuation Routes', description: 'Identifying routes.', loomVideoUrl: 'https://www.loom.com/share/example3',
    },
  ]);
  const [allMaterials, setAllMaterials] = useState([
    {
      id: 'mat1', courseId: 'c1', name: 'Safety Handbook', type: 'Document', url: 'https://example.com/safety-handbook.pdf', description: 'Main safety guide.',
    },
    {
      id: 'mat2', courseId: 'c2', name: 'Emergency Response Plan', type: 'Document', url: 'https://example.com/erp.pdf', description: 'Detailed ERP.',
    },
  ]);
  const [allAssessments, setAllAssessments] = useState([
    {
      id: 'a1',
      courseId: 'c1',
      name: 'Safety Basics Quiz',
      description: 'Test on safety fundamentals',
      questionCount: 2,
      questionsToPresent: 2,
      questionBank: [
        {
          id: 'q1', text: 'What is the capital of France?', correctAnswer: 'Paris', optionalAnswers: ['London', 'Berlin', 'Rome', 'Madrid', 'Tokyo', 'New York'],
        },
        {
          id: 'q2', text: 'Which of these is a primary color?', correctAnswer: 'Blue', optionalAnswers: ['Green', 'Orange', 'Purple', 'Brown', 'Black'],
        },
      ],
    },
    {
      id: 'a2', courseId: 'c2', name: 'Emergency Scenario Test', description: 'Practical emergency test', questionCount: 0, questionsToPresent: 0, questionBank: [],
    },
    // Example standalone items with categories
    {
      id: 'sa1', courseId: null, category: 'OJT', name: 'OJT Basics', description: 'On-the-job basics', questionCount: 0, questionsToPresent: 0, questionBank: [],
    },
    {
      id: 'sa2', courseId: null, category: 'Proficiency', name: 'Annual Proficiency', description: 'Yearly proficiency check', questionCount: 0, questionsToPresent: 0, questionBank: [],
    },
  ]);

  // Non-course assessment categories
  const [assessmentCategory, setAssessmentCategory] = useState(''); // e.g., 'OJT', 'Proficiency'

  // Handle sidebar visibility based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) { // md breakpoint
        setIsMobileSidebarOpen(false); // Close mobile sidebar if resizing to desktop
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCreateCourse = (newCourse) => {
    const newId = `c${courses.length + 1}`;
    setCourses([...courses, { ...newCourse, id: newId }]);
    setCurrentView('dashboard'); // Go back to dashboard after saving
  };

  const handleCancelCourseForm = () => {
    setCurrentView('dashboard'); // Go back to dashboard
  };

  // This function is used for navigation from sidebar or quick actions
  const handleNavigateToTab = (tabName, forceCourseSelection = false) => {
    if (forceCourseSelection) {
      setSelectedCourse(null); // Force showing course selection list
    }
    setCourseManagementActiveTab(tabName);
    setCurrentView('course-management');
    setIsMobileSidebarOpen(false); // Close mobile sidebar on navigation
  };

  const handleSelectCourse = (course) => {
    setSelectedCourse(course);
    // courseManagementActiveTab is already set by handleNavigateToTab
    setCurrentView('course-management');
    setIsMobileSidebarOpen(false); // Close mobile sidebar on selection
  };

  const handleBackToCourseSelection = () => {
    setSelectedCourse(null);
    setCurrentView('course-management'); // Go back to course list
  };

  const handleOpenAssessmentsHub = () => {
    setSelectedCourse(null);
    setAssessmentCategory('');
    setCurrentView('assessments-hub');
  };

  const handleChooseAssessmentForCourse = () => {
    setAssessmentCategory('');
    setCourseManagementActiveTab('assessments');
    setCurrentView('course-management');
  };

  const handleChooseAssessmentStandalone = () => {
    setAssessmentCategory('');
    setCurrentView('standalone-assessments');
  };

  let contentToRender;
  if (currentView === 'create-course') {
    contentToRender = (
      <CourseForm
        onSave={handleCreateCourse}
        onCancel={handleCancelCourseForm}
      />
    );
  } else if (currentView === 'dashboard') {
    contentToRender = (
      <DashboardOverview
        onCreateCourseClick={() => setCurrentView('create-course')}
        onNavigateToModulesClick={() => handleNavigateToTab('modules', true)}
        onNavigateToAssessmentsClick={() => handleNavigateToTab('assessments', true)}
        onNavigateToMaterialsClick={() => handleNavigateToTab('materials', true)}
      />
    );
  } else if (currentView === 'assessments-hub') {
    // First choose course-based or standalone
    contentToRender = (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Assessments</h2>
        <p className="text-gray-600 mb-6">Choose how you want to manage assessments.</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <button type="button" onClick={handleChooseAssessmentForCourse} className="flex-1 p-4 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors">For a Course</button>
          <button type="button" onClick={handleChooseAssessmentStandalone} className="flex-1 p-4 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors">Standalone (OJT, Proficiency, etc.)</button>
        </div>
      </div>
    );
  } else if (currentView === 'standalone-assessments') {
    // Pick category then show assessments tab only
    contentToRender = (
      <div className="bg-white rounded-lg shadow p-6 w-full">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Standalone Assessments</h2>
        <div className="mb-4">
          <label htmlFor="assessment-category" className="block text-sm font-medium text-gray-700 mb-1">
            Select Category
            <select id="assessment-category" value={assessmentCategory} onChange={(e) => setAssessmentCategory(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option value="">-- Choose category --</option>
              <option value="OJT">OJT</option>
              <option value="Proficiency">Proficiency</option>
              <option value="Certification">Certification</option>
              <option value="Recurrent">Recurrent</option>
            </select>
          </label>
        </div>

        {assessmentCategory && (
          <div className="w-full space-y-6">
            <div className="flex border-b border-gray-200 bg-gray-50">
              <button type="button" className="py-2 px-4 text-sm font-medium border-b-2 border-blue-600 text-blue-600">Assessments</button>
            </div>
            <div className="p-4">
              <div className="w-full">
                {/* Category Card */}
                <div className="w-full rounded-lg border border-gray-200 bg-white shadow">
                  <div className="px-4 py-3 border-b bg-gray-900 text-gray-100 rounded-t-lg">
                    {`Standalone Assessments - ${assessmentCategory}`}
                  </div>
                  <div className="p-4">
                    <AssessmentsTab
                      selectedCourse={null}
                      allAssessments={allAssessments}
                      setAllAssessments={setAllAssessments}
                      standaloneCategory={assessmentCategory}
                      filterCategory={assessmentCategory}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  } else { // currentView === "course-management"
    if (!selectedCourse) {
      // Display list of courses to select from
      contentToRender = (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Select a Course to Manage</h2>
          {courses.length === 0 ? (
            <p className="text-gray-600">No courses created yet. Use &quot;Create New Course&quot; to add one.</p>
          ) : (
            <ul className="space-y-3">
              {courses.map((course) => (
                <li key={course.id}>
                  <button
                    type="button"
                    onClick={() => handleSelectCourse(course)}
                    className="flex items-center w-full p-3 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors duration-200 text-left"
                  >
                    <BookOpen className="mr-3 h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-lg font-medium text-gray-800">{course.name}</p>
                      <p className="text-sm text-gray-600">{course.description}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    } else {
      // Display tabs for the selected course
      contentToRender = (
        <div className="w-full">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Managing:
              {selectedCourse.name}
            </h2>
            <button
              type="button"
              onClick={handleBackToCourseSelection}
              className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
            >
              ← Back to Courses
            </button>
          </div>
          <div className="flex border-b border-gray-200 bg-gray-50">
            <button
              type="button"
              className={`py-2 px-4 text-sm font-medium ${courseManagementActiveTab === 'modules' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
              onClick={() => setCourseManagementActiveTab('modules')}
            >
              Modules
            </button>
            <button
              type="button"
              className={`py-2 px-4 text-sm font-medium ${courseManagementActiveTab === 'lessons' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
              onClick={() => setCourseManagementActiveTab('lessons')}
            >
              Lessons
            </button>
            <button
              type="button"
              className={`py-2 px-4 text-sm font-medium ${courseManagementActiveTab === 'materials' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
              onClick={() => setCourseManagementActiveTab('materials')}
            >
              Materials
            </button>
            <button
              type="button"
              className={`py-2 px-4 text-sm font-medium ${courseManagementActiveTab === 'assessments' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
              onClick={() => setCourseManagementActiveTab('assessments')}
            >
              Assessments
            </button>
          </div>

          <div className="p-4">
            {courseManagementActiveTab === 'modules' && (
              <ModulesTab
                selectedCourse={selectedCourse}
                allModules={allModules}
                setAllModules={setAllModules}
              />
            )}
            {courseManagementActiveTab === 'lessons' && (
              <LessonsTab
                selectedCourse={selectedCourse}
                allLessons={allLessons}
                setAllLessons={setAllLessons}
              />
            )}
            {courseManagementActiveTab === 'materials' && (
              <MaterialsTab
                selectedCourse={selectedCourse}
                allMaterials={allMaterials}
                setAllMaterials={setAllMaterials}
              />
            )}
            {courseManagementActiveTab === 'assessments' && (
              <AssessmentsTab
                selectedCourse={selectedCourse}
                allAssessments={allAssessments}
                setAllAssessments={setAllAssessments}
              />
            )}
          </div>
        </div>
      );
    }
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
          aria-label="Close sidebar overlay"
        />
      )}

      <CustomSidebar
        isCollapsed={isSidebarCollapsed} // For desktop
        isMobileOpen={isMobileSidebarOpen} // For mobile
        onCloseMobile={() => setIsMobileSidebarOpen(false)} // Callback to close mobile sidebar
        currentView={currentView}
        setCurrentView={(view) => {
          if (view === 'assessments-hub') handleOpenAssessmentsHub();
          else setCurrentView(view);
        }}
        courseManagementActiveTab={courseManagementActiveTab}
        handleNavigateToTab={handleNavigateToTab}
        setSelectedCourse={setSelectedCourse} // Allow sidebar to reset selected course
      />

      <div className={`flex-1 p-6 transition-all duration-300
        ${isSidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}
        ml-0 // No margin on mobile, sidebar overlays
        w-full // Base width for small screens
        md:w-[calc(100%-4rem)] md:group-[.sidebar-collapsed]:w-[calc(100%-16rem)] // Adjust width for desktop
      `}
      >
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-800">
            {currentView === 'dashboard' && 'Admin Dashboard'}
            {currentView === 'create-course' && 'Create New Course'}
            {currentView === 'course-management' && (selectedCourse ? `Course Management: ${selectedCourse.name}` : 'Course Management')}
            {currentView === 'assessments-hub' && 'Assessments'}
            {currentView === 'standalone-assessments' && 'Standalone Assessments'}
          </h1>
          {/* Toggle button for mobile sidebar */}
          <button
            type="button"
            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            className="p-2 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 md:hidden"
            aria-label="Toggle Mobile Sidebar"
          >
            <Menu className="h-6 w-6 text-gray-600" />
          </button>
          {/* Toggle button for desktop sidebar collapse */}
          <button
            type="button"
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="p-2 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 hidden md:block"
            aria-label="Toggle Desktop Sidebar"
          >
            <Menu className="h-6 w-6 text-gray-600" />
          </button>
        </div>

        {contentToRender}
      </div>
    </div>
  );
}
